import * as XLSXModule from 'xlsx-js-style'
import { SkuMediaAnaliseResponse, BatalhaGridResponse } from './types'

const XLSX = (XLSXModule as any).default || XLSXModule

// =============================================================================
// PALETA DE CORES CORPORATIVA GID & DESIGN SYSTEM TRADE MARKETING
// =============================================================================

const COLORS = {
  headerBg: '0F172A',      // Slate 900 (Dark Slate GID)
  headerSubBg: '1E293B',   // Slate 800 (Navy Slate)
  metaBg: '334155',        // Slate 700
  corteBg: 'F1F5F9',       // Slate 100
  limeGid: 'A6C10B',       // Verde Neon Corporativo GID
  textDark: '0F172A',      // Texto escuro
  textMuted: '64748B',     // Texto secundário
  textWhite: 'FFFFFF',     // Branco
  borderSubtle: 'E2E8F0',  // Borda suave de células
  borderHeader: '475569',  // Borda entre colunas do cabeçalho
  zebraBg: 'F8FAFC',       // Fundo alternado linhas pares
  totalBg: 'E2E8F0',       // Fundo linha de total
  totalBorder: '0F172A',   // Borda dupla contábil totalizadores

  // Quadrantes Estratégicos Batalha Naval
  q1Bg: 'DCFCE7', q1Text: '166534', // Verde: Carros-Chefe (Estrelas)
  q2Bg: 'FEF3C7', q2Text: '92400E', // Âmbar: Oportunidades de Ouro
  q3Bg: 'DBEAFE', q3Text: '1E40AF', // Azul: Presença Consolidada
  q4Bg: 'FEE2E2', q4Text: '991B1B', // Vermelho: Cauda Longa / Críticos
}

// =============================================================================
// AUXILIARES DE ESTILIZAÇÃO E CRIAÇÃO DE CÉLULAS FORMATADAS
// =============================================================================

const getRowBorder = () => ({
  top: { style: 'thin', color: { rgb: COLORS.borderSubtle } },
  bottom: { style: 'thin', color: { rgb: COLORS.borderSubtle } },
  left: { style: 'thin', color: { rgb: COLORS.borderSubtle } },
  right: { style: 'thin', color: { rgb: COLORS.borderSubtle } },
})

const getTotalBorder = () => ({
  top: { style: 'thin', color: { rgb: '64748B' } },
  bottom: { style: 'double', color: { rgb: COLORS.totalBorder } },
  left: { style: 'thin', color: { rgb: COLORS.borderSubtle } },
  right: { style: 'thin', color: { rgb: COLORS.borderSubtle } },
})

// Células de Cabeçalho do Banner Principal
const cellBannerTitle = (val: string) => ({
  v: val,
  t: 's' as const,
  s: {
    font: { name: 'Calibri', sz: 14, bold: true, color: { rgb: COLORS.textWhite } },
    fill: { fgColor: { rgb: COLORS.headerBg } },
    alignment: { horizontal: 'center', vertical: 'center' },
  },
})

const cellBannerSubtitle = (val: string) => ({
  v: val,
  t: 's' as const,
  s: {
    font: { name: 'Calibri', sz: 11, bold: true, color: { rgb: COLORS.limeGid } },
    fill: { fgColor: { rgb: COLORS.headerSubBg } },
    alignment: { horizontal: 'center', vertical: 'center' },
  },
})

const cellBannerMeta = (val: string) => ({
  v: val,
  t: 's' as const,
  s: {
    font: { name: 'Calibri', sz: 9.5, color: { rgb: 'F1F5F9' } },
    fill: { fgColor: { rgb: COLORS.metaBg } },
    alignment: { horizontal: 'center', vertical: 'center' },
  },
})

const cellBannerCorte = (val: string) => ({
  v: val,
  t: 's' as const,
  s: {
    font: { name: 'Calibri', sz: 9, italic: true, color: { rgb: '334155' } },
    fill: { fgColor: { rgb: COLORS.corteBg } },
    alignment: { horizontal: 'center', vertical: 'center' },
    border: {
      bottom: { style: 'thin', color: { rgb: COLORS.borderSubtle } },
    },
  },
})

// Título de Seção (ex: 1. INDICADORES CHAVE, 2. RESUMO BATALHA NAVAL)
const cellSectionTitle = (val: string) => ({
  v: val,
  t: 's' as const,
  s: {
    font: { name: 'Calibri', sz: 10.5, bold: true, color: { rgb: COLORS.headerBg } },
    fill: { fgColor: { rgb: 'E2E8F0' } },
    alignment: { horizontal: 'left', vertical: 'center' },
    border: {
      left: { style: 'medium', color: { rgb: COLORS.limeGid } },
      top: { style: 'thin', color: { rgb: COLORS.borderSubtle } },
      bottom: { style: 'thin', color: { rgb: COLORS.borderSubtle } },
      right: { style: 'thin', color: { rgb: COLORS.borderSubtle } },
    },
  },
})

// Cabeçalhos de Colunas de Tabelas
const cellColHeader = (val: string, align: 'left' | 'center' | 'right' = 'center') => ({
  v: val,
  t: 's' as const,
  s: {
    font: { name: 'Calibri', sz: 10, bold: true, color: { rgb: COLORS.textWhite } },
    fill: { fgColor: { rgb: COLORS.headerSubBg } },
    alignment: { horizontal: align, vertical: 'center', wrapText: true },
    border: {
      top: { style: 'thin', color: { rgb: '64748B' } },
      bottom: { style: 'medium', color: { rgb: COLORS.limeGid } },
      left: { style: 'thin', color: { rgb: COLORS.borderHeader } },
      right: { style: 'thin', color: { rgb: COLORS.borderHeader } },
    },
  },
})

// Células de Dados (Zebra Striping)
const cellText = (
  val: any,
  isEven = false,
  align: 'left' | 'center' | 'right' = 'left',
  bold = false
) => ({
  v: val !== null && val !== undefined ? String(val) : '',
  t: 's' as const,
  s: {
    font: { name: 'Calibri', sz: 9.5, bold, color: { rgb: COLORS.textDark } },
    fill: { fgColor: { rgb: isEven ? COLORS.zebraBg : 'FFFFFF' } },
    alignment: { horizontal: align, vertical: 'center' },
    border: getRowBorder(),
  },
})

const cellMoney = (val: number, isEven = false, bold = false) => ({
  v: Number((val || 0).toFixed(2)),
  t: 'n' as const,
  z: '"R$" #,##0.00',
  s: {
    font: { name: 'Calibri', sz: 9.5, bold, color: { rgb: COLORS.textDark } },
    fill: { fgColor: { rgb: isEven ? COLORS.zebraBg : 'FFFFFF' } },
    alignment: { horizontal: 'right', vertical: 'center' },
    border: getRowBorder(),
  },
})

const cellNumber = (val: number, isEven = false, bold = false) => ({
  v: Math.round(val || 0),
  t: 'n' as const,
  z: '#,##0',
  s: {
    font: { name: 'Calibri', sz: 9.5, bold, color: { rgb: COLORS.textDark } },
    fill: { fgColor: { rgb: isEven ? COLORS.zebraBg : 'FFFFFF' } },
    alignment: { horizontal: 'right', vertical: 'center' },
    border: getRowBorder(),
  },
})

const cellPercent = (val: number, isEven = false, bold = false) => ({
  v: Number(((val || 0) / 100).toFixed(4)),
  t: 'n' as const,
  z: '0.0%',
  s: {
    font: { name: 'Calibri', sz: 9.5, bold, color: { rgb: COLORS.textDark } },
    fill: { fgColor: { rgb: isEven ? COLORS.zebraBg : 'FFFFFF' } },
    alignment: { horizontal: 'right', vertical: 'center' },
    border: getRowBorder(),
  },
})

const cellDecimal = (val: number, decimals = 1, isEven = false, bold = false) => ({
  v: Number((val || 0).toFixed(decimals)),
  t: 'n' as const,
  z: decimals === 1 ? '#,##0.0' : '#,##0.00',
  s: {
    font: { name: 'Calibri', sz: 9.5, bold, color: { rgb: COLORS.textDark } },
    fill: { fgColor: { rgb: isEven ? COLORS.zebraBg : 'FFFFFF' } },
    alignment: { horizontal: 'right', vertical: 'center' },
    border: getRowBorder(),
  },
})

// Badge Colorido para Quadrantes Batalha Naval
const cellQuadrantBadge = (quad: 'Q1' | 'Q2' | 'Q3' | 'Q4', label?: string) => {
  let bg = COLORS.q1Bg
  let text = COLORS.q1Text
  if (quad === 'Q2') {
    bg = COLORS.q2Bg
    text = COLORS.q2Text
  } else if (quad === 'Q3') {
    bg = COLORS.q3Bg
    text = COLORS.q3Text
  } else if (quad === 'Q4') {
    bg = COLORS.q4Bg
    text = COLORS.q4Text
  }

  return {
    v: label ? `${quad} - ${label}` : quad,
    t: 's' as const,
    s: {
      font: { name: 'Calibri', sz: 9.5, bold: true, color: { rgb: text } },
      fill: { fgColor: { rgb: bg } },
      alignment: { horizontal: 'center', vertical: 'center' },
      border: {
        top: { style: 'thin', color: { rgb: text } },
        bottom: { style: 'thin', color: { rgb: text } },
        left: { style: 'thin', color: { rgb: text } },
        right: { style: 'thin', color: { rgb: text } },
      },
    },
  }
}

// Célula de Variação / Delta de Crescimento Colorida
const cellDelta = (val: number, isSku = false, isEven = false) => {
  if (val === undefined || val === null || isNaN(val)) {
    return cellText('—', isEven, 'center')
  }
  const isPos = val > 0
  const isNeg = val < 0
  const textCol = isPos ? '166534' : isNeg ? '991B1B' : COLORS.textMuted
  const bgCol = isPos ? 'F0FDF4' : isNeg ? 'FEF2F2' : isEven ? COLORS.zebraBg : 'FFFFFF'
  const sinal = val > 0 ? '+' : ''
  const str = `${sinal}${val.toFixed(1).replace('.', ',')}${isSku ? ' SKUs' : '%'}`

  return {
    v: str,
    t: 's' as const,
    s: {
      font: { name: 'Calibri', sz: 9.5, bold: true, color: { rgb: textCol } },
      fill: { fgColor: { rgb: bgCol } },
      alignment: { horizontal: 'center', vertical: 'center' },
      border: getRowBorder(),
    },
  }
}

const cellTendencia = (val: number, isEven = false) => {
  if (val === undefined || val === null || isNaN(val)) {
    return cellText('Estável', isEven, 'center')
  }
  let label = '► Estável'
  let color = COLORS.textMuted
  if (val > 0.5) {
    label = '▲ Crescimento'
    color = '166534'
  } else if (val < -0.5) {
    label = '▼ Queda'
    color = '991B1B'
  }

  return {
    v: label,
    t: 's' as const,
    s: {
      font: { name: 'Calibri', sz: 9.5, bold: true, color: { rgb: color } },
      fill: { fgColor: { rgb: isEven ? COLORS.zebraBg : 'FFFFFF' } },
      alignment: { horizontal: 'center', vertical: 'center' },
      border: getRowBorder(),
    },
  }
}

// Células de Linha de Totalizador
const cellTotalLabel = (val: string, align: 'left' | 'center' = 'center') => ({
  v: val,
  t: 's' as const,
  s: {
    font: { name: 'Calibri', sz: 10, bold: true, color: { rgb: COLORS.headerBg } },
    fill: { fgColor: { rgb: COLORS.totalBg } },
    alignment: { horizontal: align, vertical: 'center' },
    border: getTotalBorder(),
  },
})

const cellTotalMoney = (val: number) => ({
  v: Number((val || 0).toFixed(2)),
  t: 'n' as const,
  z: '"R$" #,##0.00',
  s: {
    font: { name: 'Calibri', sz: 10, bold: true, color: { rgb: COLORS.headerBg } },
    fill: { fgColor: { rgb: COLORS.totalBg } },
    alignment: { horizontal: 'right', vertical: 'center' },
    border: getTotalBorder(),
  },
})

const cellTotalNumber = (val: number) => ({
  v: Math.round(val || 0),
  t: 'n' as const,
  z: '#,##0',
  s: {
    font: { name: 'Calibri', sz: 10, bold: true, color: { rgb: COLORS.headerBg } },
    fill: { fgColor: { rgb: COLORS.totalBg } },
    alignment: { horizontal: 'right', vertical: 'center' },
    border: getTotalBorder(),
  },
})

const cellTotalPercent = (val: number) => ({
  v: Number(((val || 0) / 100).toFixed(4)),
  t: 'n' as const,
  z: '0.0%',
  s: {
    font: { name: 'Calibri', sz: 10, bold: true, color: { rgb: COLORS.headerBg } },
    fill: { fgColor: { rgb: COLORS.totalBg } },
    alignment: { horizontal: 'right', vertical: 'center' },
    border: getTotalBorder(),
  },
})

const cellTotalDecimal = (val: number, decimals = 1) => ({
  v: Number((val || 0).toFixed(decimals)),
  t: 'n' as const,
  z: decimals === 1 ? '#,##0.0' : '#,##0.00',
  s: {
    font: { name: 'Calibri', sz: 10, bold: true, color: { rgb: COLORS.headerBg } },
    fill: { fgColor: { rgb: COLORS.totalBg } },
    alignment: { horizontal: 'right', vertical: 'center' },
    border: getTotalBorder(),
  },
})

/**
 * Calcula a largura ideal das colunas com base no conteúdo e cabeçalhos,
 * ignorando linhas de título mescladas para evitar distorção na coluna A.
 */
const autoFitColumns = (dataRows: any[][], minWidth = 12) => {
  if (!dataRows || dataRows.length === 0) return []

  const colWidths: number[] = []

  dataRows.forEach((row, rowIndex) => {
    if (rowIndex < 5 || row.length <= 1) return

    row.forEach((cell, colIndex) => {
      let cellStr = ''
      if (cell !== null && cell !== undefined) {
        if (typeof cell === 'object' && 'v' in cell) {
          if (typeof cell.v === 'number') {
            cellStr = cell.v.toLocaleString('pt-BR')
            if (cell.z && cell.z.includes('R$')) cellStr = `R$ ${cellStr}`
            if (cell.z && cell.z.includes('%')) cellStr = `${cellStr}%`
          } else {
            cellStr = String(cell.v)
          }
        } else {
          cellStr = String(cell)
        }
      }
      const len = cellStr.length + 3
      colWidths[colIndex] = Math.max(colWidths[colIndex] || minWidth, len)
    })
  })

  return colWidths.map((w) => ({ wch: Math.min(Math.max(w, minWidth), 68) }))
}

const quadMap: Record<string, { nome: string; diretriz: string }> = {
  Q1: {
    nome: 'Carros-Chefe (Estrelas)',
    diretriz: 'Alto Giro / Alta Cobertura: Garantir 0% ruptura, blindar espaço em gôndola e defender liderança.',
  },
  Q2: {
    nome: 'Oportunidades de Ouro',
    diretriz: 'Alto Giro / Baixa Cobertura: Expansão prioritária: cadastrar em novos clientes da carteira.',
  },
  Q3: {
    nome: 'Presença Consolidada',
    diretriz: 'Baixo Giro / Alta Cobertura: Ações de sell-out, combos, visibilidade no PDV e cross-merchandising.',
  },
  Q4: {
    nome: 'Cauda Longa / Críticos',
    diretriz: 'Baixo Giro / Baixa Cobertura: Avaliar racionalização do mix ou substituição por itens de maior tração.',
  },
}

// =============================================================================
// FUNÇÃO PRINCIPAL DE EXPORTAÇÃO EXECUTIVA PADRÃO GID
// =============================================================================

export const exportSkuAnalyticsToExcel = (
  data: SkuMediaAnaliseResponse,
  periodLabel: string,
  distributorName?: string,
  uf?: string
) => {
  const wb = XLSX.utils.book_new()
  const dataExtracao = new Date().toLocaleString('pt-BR')
  const nomeDist = distributorName || 'Todos os Distribuidores (Consolidado Nacional)'
  const ufText = uf ? `   |   UF: ${uf}` : ''

  const corteCat = data.batalhaNaval?.linhasDeCorteCategorias || { mediaPdvs: 0, mediaVendaSku: 0 }
  const corteSub = data.batalhaNaval?.linhasDeCorteSubcategorias || { mediaPdvs: 0, mediaVendaSku: 0 }
  const kpis = data.kpis
  const comp = kpis?.comparativo || ({} as any)

  // Gerador de cabeçalho corporativo mesclado
  const getHeaderCorporativo = (tituloAba: string) => [
    [cellBannerTitle('GID — SISTEMA DE GESTÃO DE INFORMAÇÕES DE DISTRIBUIDORES')],
    [cellBannerSubtitle(`RELATÓRIO EXECUTIVO: ${tituloAba.toUpperCase()}`)],
    [cellBannerMeta(`Período: ${periodLabel}   |   Distribuidor: ${nomeDist}${ufText}   |   Extraído em: ${dataExtracao}`)],
    [
      cellBannerCorte(
        `Linhas de Corte da Matriz: Cobertura Média = ${Math.round(corteCat.mediaPdvs).toLocaleString(
          'pt-BR'
        )} PDVs   |   Giro Médio Financeiro = ${corteCat.mediaVendaSku.toLocaleString('pt-BR', {
          style: 'currency',
          currency: 'BRL',
        })} / SKU`
      ),
    ],
    [],
  ]

  const getMergesHeader = (lastColIndex: number) => [
    { s: { r: 0, c: 0 }, e: { r: 0, c: lastColIndex } },
    { s: { r: 1, c: 0 }, e: { r: 1, c: lastColIndex } },
    { s: { r: 2, c: 0 }, e: { r: 2, c: lastColIndex } },
    { s: { r: 3, c: 0 }, e: { r: 3, c: lastColIndex } },
  ]

  const getRowsHeader = () => [
    { hpt: 26 }, // Título institucional
    { hpt: 22 }, // Título do relatório
    { hpt: 18 }, // Metadados
    { hpt: 18 }, // Linhas de corte
    { hpt: 10 }, // Linha vazia
  ]

  // ===========================================================================
  // 1. ABA: RESUMO EXECUTIVO (SCORECARD & QUADRANTES)
  // ===========================================================================
  const sheetResumoData: any[][] = [
    ...getHeaderCorporativo('Painel Executivo de SKUs e Sortimento'),
    [cellSectionTitle('1. INDICADORES CHAVE DE PERFORMANCE (KPIS GLOBAIS DE SELL-OUT & MIX)')],
    [
      cellColHeader('Métrica Estratégica', 'left'),
      cellColHeader('Resultado no Período', 'right'),
      cellColHeader('Variação vs Mês Anterior', 'center'),
      cellColHeader('Tendência', 'center'),
      cellColHeader('Objetivo / Conceito de Trade Marketing', 'left'),
    ],
    [
      cellText('Média de SKUs por PDV (Sortimento)', false, 'left', true),
      cellDecimal(kpis.mediaSkusPorPdv, 1, false, true),
      cellDelta(comp.mediaSkusPorPdvDelta, true, false),
      cellTendencia(comp.mediaSkusPorPdvDelta, false),
      cellText('Profundidade média de produtos faturados por cliente positivado', false),
    ],
    [
      cellText('Venda Média por SKU (Giro Financeiro)', true, 'left', true),
      cellMoney(kpis.vendaMediaPorSkuVl, true, true),
      cellDelta(comp.vendaMediaPorSkuVlDelta, false, true),
      cellTendencia(comp.vendaMediaPorSkuVlDelta, true),
      cellText('Faturamento médio gerado por cada produto ativo no mix', true),
    ],
    [
      cellText('Volume Médio por SKU (Giro Físico)', false, 'left', true),
      cellNumber(kpis.volumeMedioPorSkuCx, false, true),
      cellDelta(comp.volumeMedioPorSkuCxDelta, false, false),
      cellTendencia(comp.volumeMedioPorSkuCxDelta, false),
      cellText('Caixas médias faturadas por item comercializado no período', false),
    ],
    [
      cellText('Produtividade por Loja (R$ / SKU / PDV)', true, 'left', true),
      cellMoney(kpis.vendaMediaPorSkuPdv, true, true),
      cellText('—', true, 'center'),
      cellText('► Estável', true, 'center'),
      cellText('Ticket médio gerado por SKU em cada ponto de venda atendido', true),
    ],
    [
      cellText('Total de SKUs Ativos Comercializados', false, 'left', true),
      cellNumber(kpis.skusAtivos, false, true),
      cellDelta(comp.skusDelta, true, false),
      cellTendencia(comp.skusDelta, false),
      cellText('Produtos distintos com pelo menos 1 venda faturada no período', false),
    ],
    [
      cellText('Total de PDVs Positivados', true, 'left', true),
      cellNumber(kpis.totalPdvsPositivados, true, true),
      cellDelta(comp.pdvsDelta, false, true),
      cellTendencia(comp.pdvsDelta, true),
      cellText('Clientes únicos da carteira com compras faturadas no período', true),
    ],
    [
      cellText('Faturamento Total Consolidado', false, 'left', true),
      cellMoney(kpis.totalFaturamento, false, true),
      cellDelta(comp.faturamentoDelta, false, false),
      cellTendencia(comp.faturamentoDelta, false),
      cellText('Receita bruta consolidada de sell-out realizada no período', false),
    ],
    [
      cellText('Volume Total Comercializado', true, 'left', true),
      cellNumber(kpis.totalCaixas, true, true),
      cellDelta(comp.caixasDelta, false, true),
      cellTendencia(comp.caixasDelta, true),
      cellText('Volume físico consolidado comercializado em caixas padrão', true),
    ],
    [],
    [cellSectionTitle('2. RESUMO ESTRATÉGICO DA MATRIZ BATALHA NAVAL (4 QUADRANTES)')],
    [
      cellColHeader('Quadrante', 'center'),
      cellColHeader('Classificação Estratégica', 'left'),
      cellColHeader('Qtd Categorias', 'right'),
      cellColHeader('Faturamento Total (R$)', 'right'),
      cellColHeader('Share Faturamento (%)', 'right'),
      cellColHeader('Volume Total (Cx)', 'right'),
      cellColHeader('Diretriz Estratégica de Trade Marketing', 'left'),
    ],
  ]

  let totalCategoriasNosQuadrantes = 0
  let somaFaturamentoQuadrantes = 0
  let somaCaixasQuadrantes = 0

  ;(['Q1', 'Q2', 'Q3', 'Q4'] as const).forEach((qKey, qIdx) => {
    const isEven = qIdx % 2 === 1
    const catsNoQuad = (data.batalhaNaval?.categorias || []).filter((c) => c.quadrante === qKey)
    const fatQuad = catsNoQuad.reduce((acc, c) => acc + c.faturamento, 0)
    const cxQuad = catsNoQuad.reduce((acc, c) => acc + c.caixas, 0)
    const shareQuad = kpis.totalFaturamento > 0 ? (fatQuad / kpis.totalFaturamento) * 100 : 0

    totalCategoriasNosQuadrantes += catsNoQuad.length
    somaFaturamentoQuadrantes += fatQuad
    somaCaixasQuadrantes += cxQuad

    sheetResumoData.push([
      cellQuadrantBadge(qKey),
      cellText(quadMap[qKey].nome, isEven, 'left', true),
      cellNumber(catsNoQuad.length, isEven),
      cellMoney(fatQuad, isEven),
      cellPercent(shareQuad, isEven),
      cellNumber(cxQuad, isEven),
      cellText(quadMap[qKey].diretriz, isEven),
    ])
  })

  // Linha Total dos Quadrantes
  sheetResumoData.push([
    cellTotalLabel('TOTAL'),
    cellTotalLabel('Todas as Categorias', 'left'),
    cellTotalNumber(totalCategoriasNosQuadrantes),
    cellTotalMoney(somaFaturamentoQuadrantes),
    cellTotalPercent(100),
    cellTotalNumber(somaCaixasQuadrantes),
    cellTotalLabel('Consolidação de todas as categorias do portfólio', 'left'),
  ])

  sheetResumoData.push(
    [],
    [cellSectionTitle('3. REGRAS DE CORTE & METODOLOGIA DA MATRIZ BATALHA NAVAL')],
    [
      cellColHeader('Dimensão / Parâmetro', 'left'),
      cellColHeader('Linha de Corte', 'right'),
      cellColHeader('Unidade', 'center'),
      cellColHeader('Critério Metodológico', 'left'),
    ],
    [
      cellText('Eixo X: Cobertura Categorias', false, 'left', true),
      cellNumber(corteCat.mediaPdvs, false),
      cellText('PDVs positivados', false, 'center'),
      cellText('Média de clientes atendidos por categoria no período', false),
    ],
    [
      cellText('Eixo Y: Giro Financeiro Categorias', true, 'left', true),
      cellMoney(corteCat.mediaVendaSku, true),
      cellText('R$ / SKU ativo', true, 'center'),
      cellText('Faturamento total dividido pelo total de SKUs ativos', true),
    ],
    [
      cellText('Eixo X: Cobertura Subcategorias', false, 'left', true),
      cellNumber(corteSub.mediaPdvs, false),
      cellText('PDVs positivados', false, 'center'),
      cellText('Média de clientes atendidos por subcategoria/família', false),
    ],
    [
      cellText('Eixo Y: Giro Financeiro Subcategorias', true, 'left', true),
      cellMoney(corteSub.mediaVendaSku, true),
      cellText('R$ / SKU ativo', true, 'center'),
      cellText('Faturamento total dividido pelo total de SKUs ativos', true),
    ]
  )

  const wsResumo = XLSX.utils.aoa_to_sheet(sheetResumoData)
  wsResumo['!cols'] = autoFitColumns(sheetResumoData, 18)
  wsResumo['!merges'] = [
    ...getMergesHeader(4),
    { s: { r: 5, c: 0 }, e: { r: 5, c: 4 } },   // Título Seção 1
    { s: { r: 15, c: 0 }, e: { r: 15, c: 6 } }, // Título Seção 2
    { s: { r: 23, c: 0 }, e: { r: 23, c: 3 } }, // Título Seção 3
  ]
  wsResumo['!rows'] = [
    ...getRowsHeader(),
    { hpt: 22 }, // Título Seção 1
    { hpt: 22 }, // Header colunas
    { hpt: 19 }, { hpt: 19 }, { hpt: 19 }, { hpt: 19 }, { hpt: 19 }, { hpt: 19 }, { hpt: 19 }, { hpt: 19 },
    { hpt: 12 }, // Vazio
    { hpt: 22 }, // Título Seção 2
    { hpt: 22 }, // Header colunas
    { hpt: 20 }, { hpt: 20 }, { hpt: 20 }, { hpt: 20 },
    { hpt: 22 }, // Total
    { hpt: 12 }, // Vazio
    { hpt: 22 }, // Título Seção 3
    { hpt: 22 }, // Header colunas
    { hpt: 19 }, { hpt: 19 }, { hpt: 19 }, { hpt: 19 },
  ]
  XLSX.utils.book_append_sheet(wb, wsResumo, 'Resumo Executivo')

  // ===========================================================================
  // 2. ABA: BATALHA NAVAL - CATEGORIAS
  // ===========================================================================
  const cats = data.batalhaNaval?.categorias || []
  const sortedCats = [...cats].sort((a, b) => b.faturamento - a.faturamento)

  const sheetCatsData: any[][] = [
    ...getHeaderCorporativo('Batalha Naval — Análise por Categoria'),
    [
      cellColHeader('Pos', 'center'),
      cellColHeader('Quadrante', 'center'),
      cellColHeader('Categoria', 'left'),
      cellColHeader('SKUs Ativos', 'right'),
      cellColHeader('PDVs Positivados', 'right'),
      cellColHeader('Penetração na Base (%)', 'right'),
      cellColHeader('Média SKUs/PDV', 'right'),
      cellColHeader('Venda Média / PDV (R$)', 'right'),
      cellColHeader('Volume Médio / SKU (Cx)', 'right'),
      cellColHeader('Faturamento Total (R$)', 'right'),
      cellColHeader('Share Faturamento (%)', 'right'),
      cellColHeader('Volume Total (Cx)', 'right'),
      cellColHeader('Diretriz Estratégica Recomendada', 'left'),
    ],
  ]

  sortedCats.forEach((c, idx) => {
    const isEven = idx % 2 === 1
    sheetCatsData.push([
      cellText(idx + 1, isEven, 'center'),
      cellQuadrantBadge(c.quadrante, quadMap[c.quadrante]?.nome.split(' ')[0]),
      cellText(c.categoria, isEven, 'left', true),
      cellNumber(c.skusAtivos, isEven),
      cellNumber(c.pdvsPositivados, isEven),
      cellPercent(c.penetracaoPct, isEven),
      cellDecimal(c.mediaSkusPorPdv, 1, isEven),
      cellMoney(c.vendaMediaPorSku, isEven),
      cellNumber(c.volumeMedioPorSku, isEven),
      cellMoney(c.faturamento, isEven),
      cellPercent(c.shareFaturamento, isEven),
      cellNumber(c.caixas, isEven),
      cellText(quadMap[c.quadrante]?.diretriz || '', isEven),
    ])
  })

  // Linha de Totais / Médias no rodapé
  sheetCatsData.push([
    cellTotalLabel('—'),
    cellTotalLabel('TOTAL GERAL'),
    cellTotalLabel(`${cats.length} Categorias`, 'left'),
    cellTotalNumber(kpis.skusAtivos),
    cellTotalNumber(kpis.totalPdvsPositivados),
    cellTotalPercent(100),
    cellTotalDecimal(kpis.mediaSkusPorPdv, 1),
    cellTotalMoney(kpis.vendaMediaPorSkuVl),
    cellTotalNumber(kpis.volumeMedioPorSkuCx),
    cellTotalMoney(kpis.totalFaturamento),
    cellTotalPercent(100),
    cellTotalNumber(kpis.totalCaixas),
    cellTotalLabel('Consolidação de todas as categorias ativas no período', 'left'),
  ])

  const wsCats = XLSX.utils.aoa_to_sheet(sheetCatsData)
  wsCats['!cols'] = autoFitColumns(sheetCatsData, 14)
  wsCats['!merges'] = getMergesHeader(12)
  wsCats['!rows'] = [
    ...getRowsHeader(),
    { hpt: 24 }, // Header colunas
    ...sortedCats.map(() => ({ hpt: 20 })),
    { hpt: 22 }, // Total
  ]
  XLSX.utils.book_append_sheet(wb, wsCats, 'Batalha Naval - Categorias')

  // ===========================================================================
  // 3. ABA: BATALHA NAVAL - SUBCATEGORIAS / FAMÍLIAS
  // ===========================================================================
  const subcats = data.batalhaNaval?.subcategorias || []
  const sortedSubcats = [...subcats].sort((a, b) => b.faturamento - a.faturamento)

  const sheetSubData: any[][] = [
    ...getHeaderCorporativo('Batalha Naval — Análise por Subcategoria / Família'),
    [
      cellColHeader('Pos', 'center'),
      cellColHeader('Quadrante', 'center'),
      cellColHeader('Categoria Macro', 'left'),
      cellColHeader('Subcategoria / Família', 'left'),
      cellColHeader('SKUs Ativos', 'right'),
      cellColHeader('PDVs Positivados', 'right'),
      cellColHeader('Penetração na Base (%)', 'right'),
      cellColHeader('Média SKUs/PDV', 'right'),
      cellColHeader('Venda Média / PDV (R$)', 'right'),
      cellColHeader('Volume Médio / SKU (Cx)', 'right'),
      cellColHeader('Faturamento Total (R$)', 'right'),
      cellColHeader('Share Faturamento (%)', 'right'),
      cellColHeader('Volume Total (Cx)', 'right'),
      cellColHeader('Diretriz Estratégica Recomendada', 'left'),
    ],
  ]

  let somaSubFaturamento = 0
  let somaSubCaixas = 0

  sortedSubcats.forEach((s, idx) => {
    const isEven = idx % 2 === 1
    somaSubFaturamento += s.faturamento || 0
    somaSubCaixas += s.caixas || 0
    const shareSub = kpis.totalFaturamento > 0 ? (s.faturamento / kpis.totalFaturamento) * 100 : 0

    sheetSubData.push([
      cellText(idx + 1, isEven, 'center'),
      cellQuadrantBadge(s.quadrante, quadMap[s.quadrante]?.nome.split(' ')[0]),
      cellText(s.categoria, isEven, 'left'),
      cellText(s.familia, isEven, 'left', true),
      cellNumber(s.skusAtivos, isEven),
      cellNumber(s.pdvsPositivados, isEven),
      cellPercent(s.penetracaoPct, isEven),
      cellDecimal(s.mediaSkusPorPdv, 1, isEven),
      cellMoney(s.vendaMediaPorSku, isEven),
      cellNumber(s.volumeMedioPorSku, isEven),
      cellMoney(s.faturamento, isEven),
      cellPercent(shareSub, isEven),
      cellNumber(s.caixas, isEven),
      cellText(quadMap[s.quadrante]?.diretriz || '', isEven),
    ])
  })

  // Totalizadores Subcategorias
  sheetSubData.push([
    cellTotalLabel('—'),
    cellTotalLabel('TOTAL GERAL'),
    cellTotalLabel('Todas as Categorias', 'left'),
    cellTotalLabel(`${subcats.length} Subcategorias`, 'left'),
    cellTotalNumber(kpis.skusAtivos),
    cellTotalNumber(kpis.totalPdvsPositivados),
    cellTotalPercent(100),
    cellTotalDecimal(kpis.mediaSkusPorPdv, 1),
    cellTotalMoney(kpis.vendaMediaPorSkuVl),
    cellTotalNumber(kpis.volumeMedioPorSkuCx),
    cellTotalMoney(somaSubFaturamento),
    cellTotalPercent(100),
    cellTotalNumber(somaSubCaixas),
    cellTotalLabel('Consolidação de famílias comercializadas no período', 'left'),
  ])

  const wsSubs = XLSX.utils.aoa_to_sheet(sheetSubData)
  wsSubs['!cols'] = autoFitColumns(sheetSubData, 14)
  wsSubs['!merges'] = getMergesHeader(13)
  wsSubs['!rows'] = [
    ...getRowsHeader(),
    { hpt: 24 }, // Header colunas
    ...sortedSubcats.map(() => ({ hpt: 19 })),
    { hpt: 22 }, // Total
  ]
  XLSX.utils.book_append_sheet(wb, wsSubs, 'Subcategorias & Famílias')

  // ===========================================================================
  // 4. ABA: CATÁLOGO COMPLETO DE SKUS (DRILL-DOWN DETALHADO)
  // ===========================================================================
  const sheetSkusData: any[][] = [
    ...getHeaderCorporativo('Catálogo Analítico Detalhado de SKUs'),
    [
      cellColHeader('Pos', 'center'),
      cellColHeader('Categoria', 'left'),
      cellColHeader('Subcategoria / Família', 'left'),
      cellColHeader('Código SKU', 'center'),
      cellColHeader('Descrição do Produto', 'left'),
      cellColHeader('Marca', 'center'),
      cellColHeader('PDVs Positivados', 'right'),
      cellColHeader('Penetração na Base (%)', 'right'),
      cellColHeader('Volume Total (Cx)', 'right'),
      cellColHeader('Faturamento Total (R$)', 'right'),
      cellColHeader('Share Faturamento (%)', 'right'),
      cellColHeader('Produtividade (R$/PDV)', 'right'),
    ],
  ]

  const allSkusFlat: Array<{
    categoria: string
    familia: string
    sku: string
    descricao: string
    marca?: string
    pdvsPositivados: number
    penetracaoPct: number
    caixas: number
    faturamento: number
  }> = []

  data.hierarquia?.forEach((cat) => {
    cat.subcategorias?.forEach((sub) => {
      sub.skus?.forEach((sku) => {
        allSkusFlat.push({
          categoria: cat.categoria,
          familia: sub.familia,
          sku: sku.sku,
          descricao: sku.descricao,
          marca: sku.marca || 'GSA',
          pdvsPositivados: sku.pdvsPositivados || 0,
          penetracaoPct: sku.penetracaoPct || 0,
          caixas: sku.caixas || 0,
          faturamento: sku.faturamento || 0,
        })
      })
    })
  })

  allSkusFlat.sort((a, b) => b.faturamento - a.faturamento)

  let somaFaturamentoSkus = 0
  let somaCaixasSkus = 0

  allSkusFlat.forEach((item, idx) => {
    const isEven = idx % 2 === 1
    somaFaturamentoSkus += item.faturamento
    somaCaixasSkus += item.caixas
    const shareSku = kpis.totalFaturamento > 0 ? (item.faturamento / kpis.totalFaturamento) * 100 : 0
    const ticketPdv = item.pdvsPositivados > 0 ? item.faturamento / item.pdvsPositivados : 0

    sheetSkusData.push([
      cellText(idx + 1, isEven, 'center'),
      cellText(item.categoria, isEven, 'left'),
      cellText(item.familia, isEven, 'left'),
      cellText(item.sku, isEven, 'center', true),
      cellText(item.descricao, isEven, 'left', true),
      cellText(item.marca, isEven, 'center'),
      cellNumber(item.pdvsPositivados, isEven),
      cellPercent(item.penetracaoPct, isEven),
      cellNumber(item.caixas, isEven),
      cellMoney(item.faturamento, isEven),
      cellPercent(shareSku, isEven),
      cellMoney(ticketPdv, isEven),
    ])
  })

  sheetSkusData.push([
    cellTotalLabel('—'),
    cellTotalLabel('TOTAL GERAL'),
    cellTotalLabel('Catálogo Completo', 'left'),
    cellTotalLabel(`${allSkusFlat.length} SKUs`, 'center'),
    cellTotalLabel('Consolidação de Itens Ativos', 'left'),
    cellTotalLabel('GSA', 'center'),
    cellTotalNumber(kpis.totalPdvsPositivados),
    cellTotalPercent(100),
    cellTotalNumber(somaCaixasSkus),
    cellTotalMoney(somaFaturamentoSkus),
    cellTotalPercent(100),
    cellTotalMoney(kpis.vendaMediaPorSkuPdv),
  ])

  const wsSkus = XLSX.utils.aoa_to_sheet(sheetSkusData)
  wsSkus['!cols'] = autoFitColumns(sheetSkusData, 14)
  wsSkus['!merges'] = getMergesHeader(11)
  wsSkus['!rows'] = [
    ...getRowsHeader(),
    { hpt: 24 }, // Header colunas
    ...allSkusFlat.map(() => ({ hpt: 19 })),
    { hpt: 22 }, // Total
  ]
  XLSX.utils.book_append_sheet(wb, wsSkus, 'Catálogo de SKUs')

  // ===========================================================================
  // 5. ABA: PERFORMANCE DE MIX POR DISTRIBUIDOR
  // ===========================================================================
  const dists = data.distribuidores || []
  const sortedDists = [...dists].sort((a, b) => b.faturamento - a.faturamento)

  const sheetDistsData: any[][] = [
    ...getHeaderCorporativo('Desempenho de Mix & Sortimento por Distribuidor'),
    [
      cellColHeader('Pos', 'center'),
      cellColHeader('Distribuidor', 'left'),
      cellColHeader('Regional', 'center'),
      cellColHeader('PDVs Positivados', 'right'),
      cellColHeader('SKUs Ativos', 'right'),
      cellColHeader('Média SKUs / PDV (Sortimento)', 'right'),
      cellColHeader('Classificação do Mix', 'center'),
      cellColHeader('Venda Média / PDV (R$)', 'right'),
      cellColHeader('Volume Médio / SKU (Cx)', 'right'),
      cellColHeader('Volume Total (Cx)', 'right'),
      cellColHeader('Faturamento Total (R$)', 'right'),
      cellColHeader('Share Nacional (%)', 'right'),
    ],
  ]

  let somaDistFat = 0
  let somaDistCx = 0

  sortedDists.forEach((d, idx) => {
    const isEven = idx % 2 === 1
    somaDistFat += d.faturamento || 0
    somaDistCx += d.caixas || 0
    const mediaPdv = d.mediaSkusPorPdv || 0
    let classificacao = 'Mix Raso (< 5 SKUs)'
    if (mediaPdv >= 8) classificacao = 'Mix Profundo (≥ 8 SKUs)'
    else if (mediaPdv >= 5) classificacao = 'Mix Médio (5 a 8 SKUs)'

    const shareNacional = kpis.totalFaturamento > 0 ? (d.faturamento / kpis.totalFaturamento) * 100 : 0

    sheetDistsData.push([
      cellText(idx + 1, isEven, 'center'),
      cellText(d.distribuidor, isEven, 'left', true),
      cellText(d.regional || '—', isEven, 'center'),
      cellNumber(d.totalPdvs, isEven),
      cellNumber(d.skusAtivos, isEven),
      cellDecimal(mediaPdv, 1, isEven),
      cellText(classificacao, isEven, 'center', true),
      cellMoney(d.vendaMediaPorSku, isEven),
      cellNumber(d.volumeMedioPorSku, isEven),
      cellNumber(d.caixas, isEven),
      cellMoney(d.faturamento, isEven),
      cellPercent(shareNacional, isEven),
    ])
  })

  // Totalizadores Distribuidores
  sheetDistsData.push([
    cellTotalLabel('—'),
    cellTotalLabel('TOTAL GERAL / MÉDIA NACIONAL'),
    cellTotalLabel('Todas as Regionais', 'center'),
    cellTotalNumber(kpis.totalPdvsPositivados),
    cellTotalNumber(kpis.skusAtivos),
    cellTotalDecimal(kpis.mediaSkusPorPdv, 1),
    cellTotalLabel('Consolidado Nacional', 'center'),
    cellTotalMoney(kpis.vendaMediaPorSkuVl),
    cellTotalNumber(kpis.volumeMedioPorSkuCx),
    cellTotalNumber(somaDistCx),
    cellTotalMoney(somaDistFat),
    cellTotalPercent(100),
  ])

  const wsDists = XLSX.utils.aoa_to_sheet(sheetDistsData)
  wsDists['!cols'] = autoFitColumns(sheetDistsData, 14)
  wsDists['!merges'] = getMergesHeader(11)
  wsDists['!rows'] = [
    ...getRowsHeader(),
    { hpt: 24 }, // Header colunas
    ...sortedDists.map(() => ({ hpt: 19 })),
    { hpt: 22 }, // Total
  ]
  XLSX.utils.book_append_sheet(wb, wsDists, 'Mix por Distribuidor')

  // ===========================================================================
  // DOWNLOAD DO ARQUIVO COM NOME CORPORATIVO
  // ===========================================================================
  const nomeLimpoDist = nomeDist
    .replace(/[^a-zA-Z0-9]/g, '_')
    .substring(0, 30)
    .replace(/_+/g, '_')
  const nomePeriodo = periodLabel.replace(/[^a-zA-Z0-9]/g, '_').replace(/_+/g, '_')
  const nomeArquivo = `GID_Analise_SKUs_Batalha_Naval_${nomeLimpoDist}_${nomePeriodo}.xlsx`

  XLSX.writeFile(wb, nomeArquivo)
}

// =============================================================================
// EXPORTAÇÃO DA GRADE INTERATIVA DE BATALHA NAVAL
// =============================================================================

export const exportBatalhaNavalToExcel = (
  gridData: BatalhaGridResponse,
  periodLabel: string,
  nomeDist: string,
  eixosInvertidos: boolean
) => {
  const wb = XLSX.utils.book_new()

  const rawLinhas = eixosInvertidos ? gridData.produtos : gridData.geografias
  const rawColunas = eixosInvertidos ? gridData.geografias : gridData.produtos

  // Ordenar colunas pelo maior % de hit primeiro
  const colunas = [...rawColunas].sort((a, b) => {
    const statsA = eixosInvertidos ? gridData.statsGeografias[a.id] : gridData.statsProdutos[a.id]
    const statsB = eixosInvertidos ? gridData.statsGeografias[b.id] : gridData.statsProdutos[b.id]
    const hitA = statsA?.hitRate ?? 0
    const hitB = statsB?.hitRate ?? 0
    if (hitB !== hitA) return hitB - hitA
    const fatA = statsA?.faturamento ?? 0
    const fatB = statsB?.faturamento ?? 0
    if (fatB !== fatA) return fatB - fatA
    return a.nome.localeCompare(b.nome)
  })

  // Ordenar linhas pelo maior % de hit primeiro
  const linhas = [...rawLinhas].sort((a, b) => {
    const statsA = eixosInvertidos ? gridData.statsProdutos[a.id] : gridData.statsGeografias[a.id]
    const statsB = eixosInvertidos ? gridData.statsProdutos[b.id] : gridData.statsGeografias[b.id]
    const hitA = statsA?.hitRate ?? 0
    const hitB = statsB?.hitRate ?? 0
    if (hitB !== hitA) return hitB - hitA
    const fatA = statsA?.faturamento ?? 0
    const fatB = statsB?.faturamento ?? 0
    if (fatB !== fatA) return fatB - fatA
    return a.nome.localeCompare(b.nome)
  })

  const labelLinhas = eixosInvertidos
    ? `PRODUTO (${gridData.nivelProduto.toUpperCase()})`
    : `GEOGRAFIA / DISTRIBUIÇÃO (${gridData.dimensaoGeo.toUpperCase()})`
  const labelColunas = eixosInvertidos
    ? `GEOGRAFIA / DISTRIBUIÇÃO (${gridData.dimensaoGeo.toUpperCase()})`
    : `PRODUTO (${gridData.nivelProduto.toUpperCase()})`

  const totalCols = Math.max(colunas.length + 2, 6)

  const sheetData: any[][] = []

  // Header banner
  sheetData.push([cellBannerTitle('GID TRADE MARKETING — TABULEIRO DE BATALHA NAVAL')])
  sheetData.push([cellBannerSubtitle(`GRADE DE POSITIVAÇÃO & COBERTURA DE SORTIMENTO — ${labelLinhas} × ${labelColunas}`)])
  sheetData.push([cellBannerMeta(`Período de Extração: ${periodLabel} | Escopo: ${nomeDist} | Gerado em: ${new Date().toLocaleDateString('pt-BR')}`)])
  sheetData.push([cellBannerCorte(`Total de Células: ${gridData.totais.totalCombinacoes} | Positivadas: ${gridData.totais.totalPositivadas} | Taxa de Positivação Global: ${gridData.totais.hitRateGeral}% | Faturamento Total: R$ ${(gridData.totais.faturamentoTotal || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`)])

  // Col Header
  const headerRow: any[] = [
    cellColHeader(labelLinhas, 'left')
  ]
  colunas.forEach(c => {
    headerRow.push(cellColHeader(c.nome, 'center'))
  })
  headerRow.push(cellColHeader('% COBERTURA (HIT RATE)', 'right'))
  sheetData.push(headerRow)

  // Data rows
  linhas.forEach((rowItem, idx) => {
    const isEven = idx % 2 === 1
    const rowCells: any[] = [
      cellText(rowItem.nome, isEven, 'left', true)
    ]

    colunas.forEach(colItem => {
      const gId = eixosInvertidos ? colItem.id : rowItem.id
      const pId = eixosInvertidos ? rowItem.id : colItem.id
      const key = `${gId}__${pId}`
      const cellData = gridData.cells[key]

      if (cellData?.hasPositivacao) {
        rowCells.push({
          v: `${cellData.pdvsPositivados} PDV(s)`,
          t: 's' as const,
          s: {
            font: { name: 'Calibri', sz: 9.5, bold: true, color: { rgb: COLORS.q1Text } },
            fill: { fgColor: { rgb: COLORS.q1Bg } },
            alignment: { horizontal: 'center', vertical: 'center' },
            border: getRowBorder(),
          }
        })
      } else {
        rowCells.push({
          v: '—',
          t: 's' as const,
          s: {
            font: { name: 'Calibri', sz: 9, color: { rgb: '94A3B8' } },
            fill: { fgColor: { rgb: isEven ? COLORS.zebraBg : 'FFFFFF' } },
            alignment: { horizontal: 'center', vertical: 'center' },
            border: getRowBorder(),
          }
        })
      }
    })

    // Hit Rate Linha
    const statsLinha = eixosInvertidos
      ? gridData.statsProdutos[rowItem.id]
      : gridData.statsGeografias[rowItem.id]
    const hitRate = statsLinha?.hitRate || 0
    rowCells.push(cellTotalPercent(hitRate))

    sheetData.push(rowCells)
  })

  // Linha de Total / Hit Rate das Colunas
  const totalRow: any[] = [
    cellTotalLabel('% COBERTURA DA COLUNA (HIT RATE)')
  ]
  colunas.forEach(colItem => {
    const statsCol = eixosInvertidos
      ? gridData.statsGeografias[colItem.id]
      : gridData.statsProdutos[colItem.id]
    const hitRate = statsCol?.hitRate || 0
    totalRow.push(cellTotalPercent(hitRate))
  })
  totalRow.push(cellTotalPercent(gridData.totais.hitRateGeral))
  sheetData.push(totalRow)

  const ws = XLSX.utils.aoa_to_sheet(sheetData)
  ws['!merges'] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: totalCols - 1 } },
    { s: { r: 1, c: 0 }, e: { r: 1, c: totalCols - 1 } },
    { s: { r: 2, c: 0 }, e: { r: 2, c: totalCols - 1 } },
    { s: { r: 3, c: 0 }, e: { r: 3, c: totalCols - 1 } },
  ]
  ws['!cols'] = [
    { wch: 32 },
    ...colunas.map(() => ({ wch: 20 })),
    { wch: 22 },
  ]
  ws['!rows'] = [
    { hpt: 28 },
    { hpt: 20 },
    { hpt: 18 },
    { hpt: 18 },
    { hpt: 24 },
    ...linhas.map(() => ({ hpt: 19 })),
    { hpt: 22 },
  ]

  XLSX.utils.book_append_sheet(wb, ws, 'Batalha Naval Grid')

  const nomeLimpoDist = nomeDist.replace(/[^a-zA-Z0-9]/g, '_').substring(0, 30).replace(/_+/g, '_')
  const nomePeriodo = periodLabel.replace(/[^a-zA-Z0-9]/g, '_').replace(/_+/g, '_')
  const nomeArquivo = `GID_Batalha_Naval_${gridData.nivelProduto}_vs_${gridData.dimensaoGeo}_${nomeLimpoDist}_${nomePeriodo}.xlsx`

  XLSX.writeFile(wb, nomeArquivo)
}

// =============================================================================
// EXPORTAÇÃO EXECUTIVA DO RANKING POR ESTADO (MULTI-ABA COM RESUMO E UFS)
// =============================================================================

export interface RankingPorEstadoExcelParams {
  viewMode: 'marca' | 'familia'
  itemsGerais: Array<{
    displayName: string
    faturamento: number
    caixas: number
  }>
  rankingUfs?: {
    marcas: Array<{ marca: string; uf: string; faturamento: number; caixas: number; pdvs?: number }>
    familias: Array<{ familia: string; uf: string; faturamento: number; caixas: number; pdvs?: number }>
  }
  periodLabel: string
  selectedDistributorName?: string
  ufFiltro?: string
}

export const exportRankingPorEstadoToExcel = ({
  viewMode,
  itemsGerais,
  rankingUfs,
  periodLabel,
  selectedDistributorName = 'Todos os Distribuidores (Nacional)',
  ufFiltro,
}: RankingPorEstadoExcelParams) => {
  const wb = XLSX.utils.book_new()
  const dataExtracao = new Date().toLocaleString('pt-BR')
  const isMarca = viewMode === 'marca'
  const nomeTipo = isMarca ? 'Marcas' : 'Famílias'
  const labelDescricao = isMarca ? 'Descrição (Marca)' : 'Descrição (Família)'

  // Lista bruta de itens por UF
  const rawUfList = isMarca
    ? (rankingUfs?.marcas || []).map((r) => ({
        nome: r.marca,
        uf: r.uf,
        faturamento: r.faturamento,
        caixas: r.caixas,
      }))
    : (rankingUfs?.familias || []).map((r) => ({
        nome: r.familia,
        uf: r.uf,
        faturamento: r.faturamento,
        caixas: r.caixas,
      }))

  // Agrupamento por Estado (apenas estados com presença)
  const ufsMap = new Map<string, Array<{ nome: string; faturamento: number; caixas: number }>>()
  rawUfList.forEach((item) => {
    const ufKey = String(item.uf || '').trim().toUpperCase()
    if (!ufKey || ufKey === 'N/A' || ufKey === 'N/' || ufKey === 'BR' || ufKey === 'NULL') return
    if (ufFiltro && ufFiltro.trim().toUpperCase() !== ufKey) return

    if (!ufsMap.has(ufKey)) ufsMap.set(ufKey, [])
    ufsMap.get(ufKey)!.push({
      nome: item.nome,
      faturamento: item.faturamento,
      caixas: item.caixas,
    })
  })

  // ===========================================================================
  // 1. ABA RESUMO: RANKING TOTAL CONSOLIDADO
  // ===========================================================================
  const resumoData: any[][] = [
    [cellBannerTitle('GID — SISTEMA DE GESTÃO DE INFORMAÇÕES DE DISTRIBUIDORES')],
    [cellBannerSubtitle(`RANKING CONSOLIDADO: ${nomeTipo.toUpperCase()}`)],
    [cellBannerMeta(`Período / Amostragem: ${periodLabel}   |   Extraído em: ${dataExtracao}`)],
    [
      cellBannerMeta(
        `Observação: Amostragem referente ao período de ${periodLabel} • Base: ${selectedDistributorName}${
          ufFiltro ? ` • Filtro UF: ${ufFiltro}` : ''
        }`
      ),
    ],
    [],
    [
      cellColHeader(labelDescricao, 'left'),
      cellColHeader('Faturamento em Caixas (CX)', 'right'),
      cellColHeader('Faturamento em R$', 'right'),
    ],
  ]

  // Ordena itens gerais decrescente por Faturamento em R$
  const sortedGerais = [...itemsGerais].sort((a, b) => (b.faturamento || 0) - (a.faturamento || 0))
  let totalFatGeral = 0
  let totalCxGeral = 0

  sortedGerais.forEach((item, idx) => {
    const isEven = idx % 2 === 1
    totalFatGeral += Number(item.faturamento || 0)
    totalCxGeral += Number(item.caixas || 0)

    resumoData.push([
      cellText(item.displayName, isEven, 'left', true),
      cellNumber(item.caixas, isEven),
      cellMoney(item.faturamento, isEven),
    ])
  })

  // Linha de Total Consolidado
  resumoData.push([
    cellTotalLabel('TOTAL CONSOLIDADO', 'left'),
    cellTotalNumber(totalCxGeral),
    cellTotalMoney(totalFatGeral),
  ])

  const wsResumo = XLSX.utils.aoa_to_sheet(resumoData)
  wsResumo['!merges'] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: 2 } },
    { s: { r: 1, c: 0 }, e: { r: 1, c: 2 } },
    { s: { r: 2, c: 0 }, e: { r: 2, c: 2 } },
    { s: { r: 3, c: 0 }, e: { r: 3, c: 2 } },
  ]
  wsResumo['!cols'] = [{ wch: 38 }, { wch: 28 }, { wch: 26 }]
  wsResumo['!rows'] = [
    { hpt: 26 },
    { hpt: 22 },
    { hpt: 18 },
    { hpt: 18 },
    { hpt: 10 },
    { hpt: 24 },
    ...sortedGerais.map(() => ({ hpt: 19 })),
    { hpt: 22 },
  ]

  XLSX.utils.book_append_sheet(wb, wsResumo, 'Resumo')

  // ===========================================================================
  // 2. ABAS POR ESTADO COM PRESENÇA
  // ===========================================================================
  // Ordena os estados pelo maior faturamento total para navegação fluida
  const ufsOrdenadas = Array.from(ufsMap.keys()).sort((a, b) => {
    const fatA = ufsMap.get(a)!.reduce((acc, x) => acc + (x.faturamento || 0), 0)
    const fatB = ufsMap.get(b)!.reduce((acc, x) => acc + (x.faturamento || 0), 0)
    return fatB - fatA
  })

  ufsOrdenadas.forEach((ufSigla) => {
    const itensUf = [...(ufsMap.get(ufSigla) || [])].sort(
      (a, b) => (b.faturamento || 0) - (a.faturamento || 0)
    )
    let totalFatUf = 0
    let totalCxUf = 0

    const estadoData: any[][] = [
      [cellBannerTitle('GID — SISTEMA DE GESTÃO DE INFORMAÇÕES DE DISTRIBUIDORES')],
      [cellBannerSubtitle(`RANKING ESTADUAL: ${ufSigla} — ${nomeTipo.toUpperCase()}`)],
      [cellBannerMeta(`Período / Amostragem: ${periodLabel}   |   Extraído em: ${dataExtracao}`)],
      [
        cellBannerMeta(
          `Observação: Amostragem referente ao estado ${ufSigla} no período de ${periodLabel} • Base: ${selectedDistributorName}`
        ),
      ],
      [],
      [
        cellColHeader(labelDescricao, 'left'),
        cellColHeader('Faturamento em Caixas (CX)', 'right'),
        cellColHeader('Faturamento em R$', 'right'),
      ],
    ]

    itensUf.forEach((item, idx) => {
      const isEven = idx % 2 === 1
      totalFatUf += Number(item.faturamento || 0)
      totalCxUf += Number(item.caixas || 0)

      estadoData.push([
        cellText(item.nome, isEven, 'left', true),
        cellNumber(item.caixas, isEven),
        cellMoney(item.faturamento, isEven),
      ])
    })

    // Total do Estado
    estadoData.push([
      cellTotalLabel(`TOTAL ${ufSigla}`, 'left'),
      cellTotalNumber(totalCxUf),
      cellTotalMoney(totalFatUf),
    ])

    const wsEstado = XLSX.utils.aoa_to_sheet(estadoData)
    wsEstado['!merges'] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: 2 } },
      { s: { r: 1, c: 0 }, e: { r: 1, c: 2 } },
      { s: { r: 2, c: 0 }, e: { r: 2, c: 2 } },
      { s: { r: 3, c: 0 }, e: { r: 3, c: 2 } },
    ]
    wsEstado['!cols'] = [{ wch: 38 }, { wch: 28 }, { wch: 26 }]
    wsEstado['!rows'] = [
      { hpt: 26 },
      { hpt: 22 },
      { hpt: 18 },
      { hpt: 18 },
      { hpt: 10 },
      { hpt: 24 },
      ...itensUf.map(() => ({ hpt: 19 })),
      { hpt: 22 },
    ]

    // Limite de 31 caracteres para nome de aba no Excel
    const safeSheetName = ufSigla.slice(0, 31)
    XLSX.utils.book_append_sheet(wb, wsEstado, safeSheetName)
  })

  // Nome do arquivo gerado
  const nomeTipoLimpo = nomeTipo.toLowerCase()
  const nomePeriodoLimpo = periodLabel.replace(/[^a-zA-Z0-9]/g, '_').replace(/_+/g, '_')
  const nomeDistLimpo = selectedDistributorName
    .replace(/[^a-zA-Z0-9]/g, '_')
    .substring(0, 20)
    .replace(/_+/g, '_')
  const ufSufixo = ufFiltro ? `_${ufFiltro}` : ''
  const nomeArquivo = `GID_Ranking_${nomeTipoLimpo}_por_Estado${ufSufixo}_${nomeDistLimpo}_${nomePeriodoLimpo}.xlsx`

  XLSX.writeFile(wb, nomeArquivo)
}

